import React, {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Chess, DEFAULT_POSITION} from "chess.js";
import {logout} from "../clients/UserClient";
import {retrieveHistory, retrieveHistoryCount, retrieveStatistics} from "../clients/HistoryClient";
import {fetchComputerGames, fetchComputerStatistics} from "../clients/ComputerGameClient";
import {levelInfo} from "../utils/engine";
import {fetchFriendStatus, sendFriendRequest} from "../clients/FriendClient";
import {GameVariants, TimeVariants} from "../utils/gameEnums";
import FriendColumn from "./FriendColumn";
import CreateGameModal from "./CreateGameModal";
import Avatar from "./ui/Avatar";
import Icon from "./ui/Icon";
import {useUser} from "../UserContext";
import usePageTheme from "../utils/usePageTheme";

const TIME_FILTERS = [{value: null, title: "All"}, TimeVariants.BULLET, TimeVariants.BLITZ, TimeVariants.RAPID, TimeVariants.CLASSIC];

const HISTORY_PAGE_SIZE = 15;
const COMPUTER_PAGE_SIZE = 8;

// One page of a list at a time. `fetchPage(limit, offset)` returns rows; we ask for one extra row to know whether
// there is an older page. Changing `key` (another player or filter) goes back to the newest games.
const usePages = (fetchPage, pageSize, key) => {
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState(null);
    const [hasOlder, setHasOlder] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const request = useRef(0);
    const lastKey = useRef(key);
    const fetchRef = useRef(fetchPage);
    fetchRef.current = fetchPage;

    useEffect(() => {
        let pageToLoad = page;
        if (lastKey.current !== key) {
            lastKey.current = key;
            if (page !== 0) {
                setPage(0);
                return;
            }
            pageToLoad = 0;
        }
        const id = ++request.current;
        // A new list starts with a spinner; turning a page keeps the old rows (dimmed) until the new ones are there
        if (pageToLoad === 0) setRows(null);
        setLoading(true);
        setError(false);
        fetchRef.current(pageSize + 1, pageToLoad * pageSize)
            .then((result) => {
                if (id !== request.current) return;
                setHasOlder(result.length > pageSize);
                setRows(result.slice(0, pageSize));
            })
            .catch(() => {
                if (id !== request.current) return;
                setRows([]);
                setHasOlder(false);
                setError(true);
            })
            .finally(() => {
                if (id === request.current) setLoading(false);
            });
    }, [key, page, pageSize]);

    const newer = useCallback(() => setPage((p) => Math.max(0, p - 1)), []);
    const older = useCallback(() => setPage((p) => p + 1), []);

    return {rows, page, hasOlder, loading, error, newer, older};
};

// "Newer / Games 16-30 of 214 / Older" under a list. Hidden when everything fits on one page.
const Pager = ({pages, pageSize, total}) => {
    if (!pages.rows || (pages.page === 0 && !pages.hasOlder)) return null;
    const from = pages.page * pageSize + 1;
    const to = pages.page * pageSize + pages.rows.length;
    return (
        <div className="history-pager">
            <button className="btn btn-sm" onClick={pages.newer} disabled={pages.page === 0 || pages.loading}>
                <Icon name="chevronLeft"/> Newer
            </button>
            <span className="small muted history-pager__info">
                {from === to ? `Game ${from}` : `Games ${from}–${to}`}{total ? ` of ${total}` : ""}
            </span>
            <button className="btn btn-sm" onClick={pages.older} disabled={!pages.hasOlder || pages.loading}>
                Older <Icon name="chevronRight"/>
            </button>
        </div>
    );
};

const relativeDate = (date) => {
    if (!date) return "";
    const value = new Date(date);
    if (Number.isNaN(value.getTime())) return "";
    const days = Math.floor((Date.now() - value.getTime()) / 86400000);
    if (days <= 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 7) return `${days} days ago`;
    return value.toLocaleDateString("en-GB", {day: "numeric", month: "short", year: days > 300 ? "numeric" : undefined});
};

const StatCard = ({statistic}) => {
    const variant = Object.values(TimeVariants).find((v) => v.value === statistic.timeVariant);
    const winrate = Math.round((statistic.winrate || 0) * 100);
    return (
        <div className="stat-card">
            <div className="stat-card__head">
                {variant && <img src={variant.imageSrc} alt=""/>}
                {variant ? variant.title : statistic.timeVariant}
            </div>
            <div className="stat-card__elo">{statistic.eloPoints}</div>
            <div className="stat-card__meta">
                <span>{statistic.gamesWon} won of {statistic.gamesPlayed}</span>
                <span>{winrate}%</span>
            </div>
            <div className="winbar" aria-label={`Win rate ${winrate}%`}>
                <div style={{width: `${winrate}%`}}/>
            </div>
        </div>
    );
};

const movesText = (moveCount) => {
    const moves = Math.ceil(moveCount / 2);
    return `${moves} ${moves === 1 ? "move" : "moves"}`;
};

const HistoryRow = ({row, username, onOpen}) => {
    const isPlayer1 = row.user1.username === username;
    const opponent = isPlayer1 ? row.user2 : row.user1;
    const color = isPlayer1 ? row.player1Color : row.player2Color;
    const resultKey = row.result === true ? "win" : row.result === false ? "loss" : "draw";
    const variant = Object.values(TimeVariants).find((v) => v.value === row.timeVariant);
    const moveCount = row.moves ? row.moves.trim().split(" ").filter(Boolean).length : 0;

    return (
        <button type="button" className="history-row" onClick={() => onOpen(row)}>
            <span className={`result-badge result-badge--${resultKey}`}>{resultKey}</span>
            <div className="history-row__opponent">
                <Avatar name={opponent.username} size={32}/>
                <div>
                    <div className="history-row__name">vs {opponent.username}</div>
                    <div className="history-row__sub">
                        {[color ? `Played ${color}` : "", movesText(moveCount), relativeDate(row.gameFinished)].filter(Boolean).join(" · ")}
                    </div>
                </div>
            </div>
            {variant ? <span className="chip"><img src={variant.imageSrc} alt=""/>{variant.title}</span> : <span/>}
            <span className="history-row__go" aria-hidden="true"><Icon name="chevronRight"/></span>
        </button>
    );
};

const formatTimeControl = (baseMs, incrementMs) => {
    if (baseMs === null || baseMs === undefined) return "No clock";
    const minutes = baseMs / 60000;
    return `${Number.isInteger(minutes) ? minutes : minutes.toFixed(1)} + ${Math.round((incrementMs || 0) / 1000)}`;
};

const ComputerGameRow = ({row, onOpen}) => {
    const moveCount = row.moves ? row.moves.trim().split(" ").filter(Boolean).length : 0;
    return (
        <button type="button" className="history-row" onClick={() => onOpen(row)}>
            <span className={`result-badge result-badge--${row.outcome}`}>{row.outcome}</span>
            <div className="history-row__opponent">
                <Avatar name="Computer" size={32} guest/>
                <div>
                    <div className="history-row__name">vs Computer, level {row.level}</div>
                    <div className="history-row__sub">
                        {[`Played ${row.playerColor}`, movesText(moveCount), relativeDate(row.gameFinished)].filter(Boolean).join(" · ")}
                    </div>
                </div>
            </div>
            <span className="chip">{row.variant === "jumpstart" ? "Jumpstart · " : ""}{formatTimeControl(row.baseTimeMs, row.incrementMs)}</span>
            <span className="history-row__go" aria-hidden="true"><Icon name="chevronRight"/></span>
        </button>
    );
};

// Games against the computer: a short record and the latest games. They never count towards a rating.
const ComputerGamesCard = ({username, ownProfile}) => {
    const navigate = useNavigate();
    const [statistics, setStatistics] = useState(null);
    const pages = usePages((limit, offset) => fetchComputerGames(username, limit, offset), COMPUTER_PAGE_SIZE, username);
    const games = pages.rows;

    useEffect(() => {
        if (!username) return;
        fetchComputerStatistics(username).then(setStatistics).catch(() => setStatistics(null));
    }, [username]);

    if (games === null) return null;
    if (games.length === 0 && !ownProfile) return null;

    const total = statistics?.total;
    const bestWin = statistics?.levels?.filter((l) => l.gamesWon > 0).map((l) => l.level).sort((a, b) => b - a)[0];

    const open = (row) => navigate("/analysis", {
        state: {
            initialFen: row.initialFen,
            moves: row.moves.trim().split(" ").filter(Boolean),
            playerColor: row.playerColor,
            opponent: {username: `Computer (level ${row.level})`, isActualUser: false},
            player: {username, isActualUser: true}
        }
    });

    return (
        <div className="card">
            <div className="card-header" style={{flexWrap: "wrap"}}>
                <div className="card-title"><Icon name="cpu"/> Against the computer</div>
                {total && total.gamesPlayed > 0 && (
                    <span className="small muted" style={{fontWeight: 700}}>
                        {total.gamesWon} won · {total.gamesLost} lost · {total.gamesDrawn} drawn
                        {bestWin ? ` · best win: level ${bestWin} (${levelInfo(bestWin).name})` : ""}
                    </span>
                )}
            </div>
            {games.length === 0 ? (
                <div className="empty-state">
                    <Icon name="cpu"/>
                    <strong>No games against the computer yet</strong>
                    <span className="small">Finished games show up here. They never change your rating.</span>
                    <button className="btn btn-sm" style={{marginTop: "0.5rem"}} onClick={() => navigate("/computer")}>
                        <Icon name="cpu"/> Play the computer
                    </button>
                </div>
            ) : (
                <>
                    <div className={`history-list ${pages.loading ? "history-list--loading" : ""}`}>
                        {games.map((row) => <ComputerGameRow key={row.gameId} row={row} onOpen={open}/>)}
                    </div>
                    <Pager pages={pages} pageSize={COMPUTER_PAGE_SIZE} total={total?.gamesPlayed}/>
                </>
            )}
        </div>
    );
};

const FriendButton = ({status, username, onChange}) => {
    const navigate = useNavigate();
    const [showChallenge, setShowChallenge] = useState(false);
    const [error, setError] = useState(null);

    if (!status) return null;

    if (status === "friends") {
        return (
            <>
                <span className="chip chip--leaf" style={{alignSelf: "center"}}><Icon name="userCheck" size={14}/> Friends</span>
                <button className="btn" onClick={() => navigate(`/messages/${encodeURIComponent(username)}`)}>
                    <Icon name="chat"/> Message
                </button>
                <button className="btn btn-primary" onClick={() => setShowChallenge(true)}>
                    <Icon name="swords"/> Challenge
                </button>
                <CreateGameModal showCreateModal={showChallenge} closeModal={() => setShowChallenge(false)} friendName={username}/>
            </>
        );
    }

    if (status === "friendshipPending") {
        return <button className="btn" disabled><Icon name="hourglass"/> Request pending</button>;
    }

    return (
        <div className="stack stack--sm" style={{alignItems: "flex-end"}}>
            <button className="btn btn-primary" onClick={() => sendFriendRequest(username)
                .then(() => onChange("friendshipPending"))
                .catch((e) => setError(e.response?.data?.message || e.message))}>
                <Icon name="userPlus"/> Add friend
            </button>
            {error && <div className="form-error">{error}</div>}
        </div>
    );
};

const PgnCard = () => {
    const navigate = useNavigate();
    const [pgnInput, setPgnInput] = useState("");
    const [error, setError] = useState(null);

    const handlePgnAnalysis = () => {
        if (!pgnInput.trim()) {
            setError("Paste a game in PGN notation first.");
            return;
        }
        try {
            const game = new Chess();
            game.loadPgn(pgnInput);
            const headers = game.header();
            const startFen = headers.FEN || DEFAULT_POSITION;
            navigate("/analysis", {
                state: {
                    moves: game.history(),
                    initialFen: startFen,
                    playerColor: "white",
                    opponent: {username: headers.Black || "Black"},
                    whiteName: headers.White || "White",
                    blackName: headers.Black || "Black",
                    isPgnAnalysis: true
                }
            });
        } catch (e) {
            setError("That does not look like a valid PGN: " + e.message);
        }
    };

    return (
        <div className="card">
            <div className="card-header">
                <div className="card-title"><Icon name="sparkles"/> Analyse a game</div>
            </div>
            <div className="card-body stack stack--sm">
                <p className="small muted">Paste any game in PGN notation to step through it with Stockfish.</p>
                <textarea className="textarea" value={pgnInput} placeholder={'1. e4 e5 2. Nf3 Nc6 3. Bb5 a6 …'}
                          onChange={(e) => {
                              setPgnInput(e.target.value);
                              setError(null);
                          }} aria-label="PGN"/>
                {error && <div className="form-error">{error}</div>}
                <button className="btn btn-secondary" onClick={handlePgnAnalysis}><Icon name="sparkles"/> Analyse</button>
            </div>
        </div>
    );
};

const Profile = ({username, ownProfile}) => {
    usePageTheme("blossom");
    const navigate = useNavigate()
    const me = useUser();
    const [statistics, setStatistics] = useState(null)
    const [gameVariant, setGameVariant] = useState(GameVariants.STANDARD)
    const [selectedTimeVariant, setSelectedTimeVariant] = useState(null);
    const [friendStatus, setFriendStatus] = useState(null)

    useEffect(() => {
        if (!ownProfile && me?.isLoggedIn && username) {
            fetchFriendStatus(username)
                .then((data) => setFriendStatus(data.friendStatus))
                .catch(() => setFriendStatus(null))
        }
    }, [ownProfile, username, me?.isLoggedIn])

    const historyPages = usePages(
        (limit, offset) => (username ? retrieveHistory(username, limit, gameVariant, selectedTimeVariant, offset) : new Promise(() => {})),
        HISTORY_PAGE_SIZE,
        `${username}|${gameVariant}|${selectedTimeVariant}`
    );
    const history = historyPages.rows;
    const [historyCount, setHistoryCount] = useState(null);
    useEffect(() => {
        if (!username) return;
        setHistoryCount(null);
        let current = true;
        retrieveHistoryCount(username, gameVariant, selectedTimeVariant)
            .then((count) => current && setHistoryCount(count))
            .catch(() => {});
        return () => {
            current = false;
        };
    }, [username, gameVariant, selectedTimeVariant]);
    const loadError = historyPages.error ? "We could not find this player's games." : null;

    useEffect(() => {
        if (!username) return;
        retrieveStatistics(username, gameVariant)
            .then(setStatistics)
            .catch(() => setStatistics([]))
    }, [username, gameVariant]);

    const goToAnalysis = (row) => {
        const isPlayer1 = username === row.user1.username;
        navigate("/analysis", {
            state: {
                initialFen: row.initialFen,
                moves: row.moves.trim().split(" ").filter(Boolean),
                playerColor: isPlayer1 ? row.player1Color : row.player2Color,
                opponent: isPlayer1 ? row.user2 : row.user1,
                player: isPlayer1 ? row.user1 : row.user2,
                gameId: row.gameId
            }
        })
    }

    const handleLogout = () => {
        logout().finally(() => navigate(0));
    };

    const totalGames = statistics?.reduce((sum, s) => sum + (s.gamesPlayed || 0), 0) || 0;

    const main = (
        <div className="stack">
            <div className="card">
                <div className="card-header" style={{flexWrap: "wrap"}}>
                    <div className="card-title"><Icon name="trophy"/> Ratings</div>
                    <div className="segmented" aria-label="Variant">
                        {[[GameVariants.STANDARD, "Standard"], [GameVariants.JUMPSTART, "Jumpstart"]].map(([value, label]) => (
                            <button key={value} type="button" className={gameVariant === value ? "active" : ""}
                                    onClick={() => setGameVariant(value)}>{label}</button>
                        ))}
                    </div>
                </div>
                <div className="card-body">
                    {statistics === null ? <span className="spinner"/> :
                        statistics.length === 0 ? (
                            <div className="empty-state">
                                <Icon name="sprout"/>
                                <strong>No rated games yet</strong>
                                <span className="small">Ratings appear after the first finished game.</span>
                            </div>
                        ) : (
                            <div className="stat-grid">
                                {statistics.map((statistic) => <StatCard key={statistic.timeVariant} statistic={statistic}/>)}
                            </div>
                        )}
                </div>
            </div>

            <div className="card">
                <div className="card-header" style={{flexWrap: "wrap"}}>
                    <div className="card-title"><Icon name="clock"/> Recent games</div>
                    <div className="segmented" aria-label="Time control">
                        {TIME_FILTERS.map((time) => (
                            <button key={time.title} type="button"
                                    className={selectedTimeVariant === time.value ? "active" : ""}
                                    onClick={() => setSelectedTimeVariant(time.value)}>
                                {time.imageSrc && <img src={time.imageSrc} alt=""/>}{time.title}
                            </button>
                        ))}
                    </div>
                </div>
                {history === null ? (
                    <div className="card-body"><span className="spinner"/></div>
                ) : history.length === 0 ? (
                    <div className="empty-state">
                        <Icon name="board"/>
                        <strong>{loadError || "No games to show"}</strong>
                        {!loadError && <span className="small">Finished games show up here. Click one to review it.</span>}
                    </div>
                ) : (
                    <>
                        <div className={`history-list ${historyPages.loading ? "history-list--loading" : ""}`}>
                            {history.map((row) => (
                                <HistoryRow key={row.gameId} row={row} username={username} onOpen={goToAnalysis}/>
                            ))}
                        </div>
                        <Pager pages={historyPages} pageSize={HISTORY_PAGE_SIZE} total={historyCount}/>
                    </>
                )}
            </div>

            <ComputerGamesCard username={username} ownProfile={ownProfile}/>
        </div>
    );

    return (
        <div className="page">
            <div className="card profile-hero">
                <div className="profile-hero__banner"/>
                <div className="profile-hero__content">
                    <Avatar name={username} size={96}/>
                    <div className="profile-hero__name">
                        <div className="eyebrow">{ownProfile ? "Your profile" : "Player"}</div>
                        <h1>{username}</h1>
                        <p className="muted small" style={{fontWeight: 700}}>
                            {totalGames} {totalGames === 1 ? "game" : "games"} played in {gameVariant}
                        </p>
                    </div>
                    <div className="profile-hero__actions">
                        {ownProfile ? (
                            <>
                                <button className="btn" onClick={() => navigate("/messages")}><Icon name="chat"/> Messages</button>
                                <button className="btn btn-ghost" onClick={handleLogout}><Icon name="logout"/> Log out</button>
                            </>
                        ) : (
                            <FriendButton status={friendStatus} username={username} onChange={setFriendStatus}/>
                        )}
                    </div>
                </div>
            </div>

            {ownProfile ? (
                <div className="profile-layout">
                    {main}
                    <aside className="stack">
                        <FriendColumn/>
                        <PgnCard/>
                    </aside>
                </div>
            ) : main}
        </div>
    )
}

export default Profile;
